import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api';

/**
 * Emergency Substitution API using RTK Query
 *
 * AUTHENTICATED API - Token required
 * Consolidated API for all emergency substitution operations:
 * - Dropdown options management
 * - Emergency request CRUD operations
 * - Candidate response management
 * - TeamTailor integration
 *
 * Follows 2025 RTK Query best practices:
 * - Centralized server state management
 * - Automatic caching and request deduplication
 * - Optimistic updates with cache invalidation
 * - LIST/ITEM tagging pattern for granular updates
 */
export const emergencyApi = createApi({
  reducerPath: 'emergencyApi',
  baseQuery,
  tagTypes: ['DropdownOptions', 'EmergencyRequests', 'CandidateResponses'],
  // Cache configuration for emergency monitoring
  keepUnusedDataFor: 300, // 5 minutes cache
  refetchOnMountOrArgChange: 60, // Refetch if data is 1 minute old
  endpoints: (builder) => ({
    // ==================== DROPDOWN OPTIONS ====================
    /**
     * Fetch dropdown options by type
     * @param {string} type - 'organization', 'titulacio', 'torn', or 'centre'
     * @returns Array of {id, name} objects
     */
    getDropdownOptions: builder.query({
      query: (type) => `/api/v1/dropdown-options/${type}/`,
      providesTags: (result, error, type) => [{ type: 'DropdownOptions', id: type }],
    }),

    /**
     * Create new dropdown option
     * @param {Object} params
     * @param {string} params.type - 'organization', 'titulacio', 'torn', or 'centre'
     * @param {string} params.name - Option name
     * @returns Created option object
     */
    createDropdownOption: builder.mutation({
      query: ({ type, name }) => ({
        url: `/api/v1/dropdown-options/${type}/create/`,
        method: 'POST',
        body: { name },
      }),
      invalidatesTags: (result, error, { type }) => [{ type: 'DropdownOptions', id: type }],
    }),

    /**
     * Update dropdown option
     * @param {Object} params
     * @param {string} params.type - 'organization', 'titulacio', 'torn', or 'centre'
     * @param {string} params.id - Option ID
     * @param {string} params.name - New option name
     * @returns Updated option object
     */
    updateDropdownOption: builder.mutation({
      query: ({ type, id, name }) => ({
        url: `/api/v1/dropdown-options/${type}/${id}/`,
        method: 'PATCH',
        body: { name },
      }),
      invalidatesTags: (result, error, { type }) => [{ type: 'DropdownOptions', id: type }],
    }),

    /**
     * Delete dropdown option
     * @param {Object} params
     * @param {string} params.type - 'organization', 'titulacio', 'torn', or 'centre'
     * @param {string} params.id - Option ID
     * @returns Success response
     */
    deleteDropdownOption: builder.mutation({
      query: ({ type, id }) => ({
        url: `/api/v1/dropdown-options/${type}/${id}/delete/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { type }) => [{ type: 'DropdownOptions', id: type }],
    }),

    // ==================== EMERGENCY REQUESTS ====================
    /**
     * Get list of emergency requests
     * Uses LIST/ITEM pattern for optimal cache updates
     */
    getEmergencyRequests: builder.query({
      query: () => '/api/v1/emergency-requests/',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'EmergencyRequests', id })),
              { type: 'EmergencyRequests', id: 'LIST' },
            ]
          : [{ type: 'EmergencyRequests', id: 'LIST' }],
    }),

    /**
     * Get emergency request by ID
     * @param {string} requestId - Emergency request UUID
     * @returns Emergency request object with details
     */
    getEmergencyRequestById: builder.query({
      query: (requestId) => `/api/v1/emergency-requests/${requestId}/`,
      providesTags: (result, error, requestId) => [
        { type: 'EmergencyRequests', id: requestId },
      ],
    }),

    /**
     * Create emergency substitution request
     * @param {Object} formData
     * @param {string} formData.organization - Organization name
     * @param {string} formData.titulacio - Degree/certification
     * @param {string} formData.torn - Shift
     * @param {string} formData.centre - Center name
     * @param {string} formData.horari - Schedule description
     * @returns Created request object with id
     */
    createEmergencyRequest: builder.mutation({
      query: (formData) => ({
        url: '/api/v1/emergency-requests/create/',
        method: 'POST',
        body: formData,
      }),
      // Only invalidate the LIST tag for optimistic UI updates
      invalidatesTags: [{ type: 'EmergencyRequests', id: 'LIST' }],
    }),

    /**
     * Update emergency request (mark as completed, etc.)
     * @param {Object} params
     * @param {string} params.requestId - Emergency request UUID
     * @param {string} [params.status] - New status (e.g., 'COMPLETED')
     * @param {string} [params.selected_substitute] - UUID of selected substitute
     * @param {string} [params.completion_comments] - Completion notes
     * @returns Updated emergency request object
     */
    updateEmergencyRequest: builder.mutation({
      query: ({ requestId, ...updates }) => ({
        url: `/api/v1/emergency-requests/${requestId}/update/`,
        method: 'PATCH',
        body: updates,
      }),
      // Invalidate both the specific item and the list
      invalidatesTags: (result, error, { requestId }) => [
        { type: 'EmergencyRequests', id: requestId },
        { type: 'EmergencyRequests', id: 'LIST' },
      ],
    }),

    /**
     * Delete emergency request
     * @param {string} requestId - Emergency request UUID
     * @returns Success response
     */
    deleteEmergencyRequest: builder.mutation({
      query: (requestId) => ({
        url: `/api/v1/emergency-requests/${requestId}/`,
        method: 'DELETE',
      }),
      // Invalidate both the specific item and the list
      invalidatesTags: (result, error, requestId) => [
        { type: 'EmergencyRequests', id: requestId },
        { type: 'EmergencyRequests', id: 'LIST' },
      ],
    }),

    /**
     * Start an async TeamTailor candidate refresh.
     * Returns 202 with { run_id, status } — poll getTeamTailorRefreshStatus for completion.
     * A 409 (a refresh is already running) also carries the existing run_id.
     */
    refreshTeamTailorCandidates: builder.mutation({
      query: () => ({
        url: '/api/v1/emergency-requests/refresh-teamtailor/',
        method: 'POST',
      }),
    }),

    /**
     * Poll the status of a TeamTailor refresh run.
     * @param {string} [runId] - Specific run; omit for the account's latest run.
     * @returns { run_id, status, statistics, error, ... }
     */
    getTeamTailorRefreshStatus: builder.query({
      query: (runId) =>
        `/api/v1/emergency-requests/refresh-teamtailor/status/${runId ? `?run_id=${runId}` : ''}`,
    }),

    // ==================== CANDIDATE RESPONSES ====================
    /**
     * Get all candidate responses for an emergency request
     * @param {string} requestId - Emergency request UUID
     * @returns Object map of candidate responses keyed by candidate_id
     */
    getCandidateResponses: builder.query({
      query: (requestId) => `/api/v1/emergency-requests/${requestId}/candidates/`,
      providesTags: (result, error, requestId) => [
        { type: 'CandidateResponses', id: 'LIST' },
        { type: 'CandidateResponses', id: requestId },
      ],
      transformResponse: (response) => {
        // Transform array to plain object for O(1) lookup by candidate_id
        // Using plain object instead of Map for Redux serialization compatibility
        const responseMap = {};
        response.forEach((item) => {
          // Backend returns candidate as nested object, so we use candidate.id
          // Safety check: skip responses without candidate data
          if (item.candidate?.id) {
            responseMap[item.candidate.id] = item;
          }
        });
        return responseMap;
      },
      // Smart polling configuration (Best Practices 2026)
      pollingInterval: 60000, // Auto-refresh every 60 seconds
      skipPollingIfUnfocused: true, // Pause polling when tab is inactive (battery-efficient)
    }),

    /**
     * Contact a candidate (creates or updates response)
     * @param {Object} params
     * @param {string} params.requestId - Emergency request UUID
     * @param {string} params.candidateId - Candidate UUID
     * @param {string} [params.contactMethod] - 'dolma' or 'whatsapp'
     * @returns Updated/created candidate response
     */
    contactCandidate: builder.mutation({
      query: ({ requestId, candidateId, contactMethod = 'dolma' }) => ({
        url: `/api/v1/emergency-requests/${requestId}/candidates/contact/`,
        method: 'POST',
        body: {
          candidate_id: candidateId,
          contact_method: contactMethod,
        },
      }),
      invalidatesTags: (result, error, { requestId }) => [
        { type: 'CandidateResponses', id: requestId },
      ],
    }),

    /**
     * Bulk contact multiple candidates at once
     * @param {Object} params
     * @param {string} params.requestId - Emergency request UUID
     * @param {string[]} params.candidate_ids - Array of candidate UUIDs
     * @returns {Object} Result with sent count, already_contacted count, failed count, and errors array
     */
    bulkContactCandidates: builder.mutation({
      query: ({ requestId, candidate_ids }) => ({
        url: `/api/v1/emergency-requests/${requestId}/contact/bulk/`,
        method: 'POST',
        body: { candidate_ids },
      }),
      invalidatesTags: (result, error, { requestId }) => [
        { type: 'CandidateResponses', id: 'LIST' },
        { type: 'CandidateResponses', id: requestId },
        { type: 'EmergencyRequests', id: requestId },
      ],
    }),

    /**
     * Update candidate response status
     * @param {Object} params
     * @param {string} params.responseId - Candidate response UUID
     * @param {Object} params.updates - Fields to update
     * @returns Updated candidate response
     */
    updateCandidateResponse: builder.mutation({
      query: ({ responseId, ...updates }) => ({
        url: `/api/v1/candidate-responses/${responseId}/`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: [{ type: 'CandidateResponses', id: 'LIST' }],
    }),
  }),
});

export const {
  // Dropdown options
  useGetDropdownOptionsQuery,
  useCreateDropdownOptionMutation,
  useUpdateDropdownOptionMutation,
  useDeleteDropdownOptionMutation,
  // Emergency requests
  useGetEmergencyRequestsQuery,
  useGetEmergencyRequestByIdQuery,
  useCreateEmergencyRequestMutation,
  useUpdateEmergencyRequestMutation,
  useDeleteEmergencyRequestMutation,
  useRefreshTeamTailorCandidatesMutation,
  useLazyGetTeamTailorRefreshStatusQuery,
  // Candidate responses
  useGetCandidateResponsesQuery,
  useLazyGetCandidateResponsesQuery,
  useContactCandidateMutation,
  useBulkContactCandidatesMutation,
  useUpdateCandidateResponseMutation,
} = emergencyApi;
